import { memo } from 'react'
import type { QuestionResponse, SubSection, SupportingTextBlock } from '@assessments/shared'
import { cn } from '@/lib/utils'
import { RichText } from '@/components/ui/RichText'
import { QuestionRenderer } from '../question-rendering/QuestionRenderer.js'
import { SaveIndicator, type SaveState } from '../response-autosave/index.js'
import { toRenderableQuestion } from './questionMapping.js'
import type { PlacedSupportingTexts } from './supportingTextVisibility.js'

export interface QuestionListProps {
  subSection: SubSection
  responses: Record<string, QuestionResponse>
  canEdit: boolean
  onChange: (questionId: string, response: QuestionResponse) => void
  /** Autosave a text answer when its input loses focus. */
  onBlur?: (questionId: string) => void
  /** Per-question autosave state, keyed by question id. */
  saveStatus?: Record<string, SaveState>
  /** Retry a question's failed autosave. */
  onRetry?: (questionId: string) => void
  /** Question ids currently listed in the last blocked Mark Complete attempt's
   * unanswered-required set. */
  flaggedQuestionIds?: Set<string>
  /** Question the respondent was just sent to by "jump to first" — flashes to show
   * where focus landed, then clears. */
  highlightedQuestionId?: string | null
  /** Questions skip logic hides; they are not rendered. */
  hiddenQuestionIds?: ReadonlySet<string>
  /** Bumped to rebuild a question's inputs from freshly loaded data, discarding their local state. */
  revisions?: Record<string, number>
  /** Visible supporting text blocks by question id; a question with no entry renders as before. */
  supportingTexts?: ReadonlyMap<string, PlacedSupportingTexts>
}

// A callout between the cards: an <li> keeps the <ol> valid, role="note" keeps it out of
// question counts, and memo keeps a keystroke elsewhere from re-sanitizing every block.
const SupportingTextNote = memo(function SupportingTextNote({
  block,
}: {
  block: SupportingTextBlock
}) {
  return (
    <li role="note" className="flex items-start gap-3 rounded-lg border border-info bg-info-bg p-4">
      <svg
        aria-hidden="true"
        viewBox="0 0 20 20"
        fill="currentColor"
        className="mt-0.5 h-5 w-5 shrink-0 text-info"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm0-11a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm-1 2a1 1 0 0 1 2 0v4a1 1 0 1 1-2 0V9Z"
        />
      </svg>
      <RichText html={block.content} className="font-semibold text-body" />
    </li>
  )
})

export function QuestionList({
  subSection,
  responses,
  canEdit,
  onChange,
  onBlur,
  saveStatus,
  onRetry,
  flaggedQuestionIds,
  highlightedQuestionId,
  hiddenQuestionIds,
  revisions,
  supportingTexts,
}: QuestionListProps) {
  if (subSection.questions.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-surface p-6 text-center text-sm text-subtle">
        This sub-section has no questions.
      </div>
    )
  }

  const questions = subSection.questions.filter((q) => !hiddenQuestionIds?.has(q.id))
  if (questions.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-surface p-6 text-center text-sm text-subtle">
        None of these questions apply based on your answers.
      </div>
    )
  }

  return (
    <ol className="flex flex-col gap-4">
      {questions.flatMap((question) => [
        ...(supportingTexts?.get(question.id)?.above ?? []).map((block) => (
          <SupportingTextNote key={`note-${block.id}`} block={block} />
        )),
        <li
          key={question.id}
          className={cn(
            'rounded-lg border bg-surface p-4 shadow-card',
            // Under reduced motion the border alone carries the highlight.
            question.id === highlightedQuestionId
              ? 'border-danger motion-safe:animate-highlight-flash'
              : 'border-border',
          )}
          // Focus-out anywhere in this question autosaves it; the handler skips unchanged
          // and non-text answers.
          onBlur={onBlur ? () => onBlur(question.id) : undefined}
        >
          {question.displayId && (
            <span className="text-xs font-semibold text-subtle">{question.displayId}</span>
          )}
          <QuestionRenderer
            key={revisions?.[question.id] ?? 0}
            question={toRenderableQuestion(question)}
            response={responses[question.id]}
            onChange={(response) => onChange(question.id, response)}
            disabled={!canEdit}
            flagged={flaggedQuestionIds?.has(question.id)}
          />
          {saveStatus && (
            <div className="mt-2">
              <SaveIndicator
                state={saveStatus[question.id] ?? 'idle'}
                onRetry={onRetry ? () => onRetry(question.id) : undefined}
              />
            </div>
          )}
        </li>,
        ...(supportingTexts?.get(question.id)?.below ?? []).map((block) => (
          <SupportingTextNote key={`note-${block.id}`} block={block} />
        )),
      ])}
    </ol>
  )
}
